import BaseInput from 'components/atoms/base-input';
import i18n from 'infrastructure/i18n';

import { FormBlock } from './form-block';
import { FormItem } from './form-item';
import HeaderBlock from './header-block';
import s from './styles.module.scss';

import type { FormikProps } from 'formik';
import type {
  ICampaignQuestionAnswersSelect,
  IOutcomesFormValues,
  TQuestionGroup,
} from 'infrastructure/interfaces';
import type { FC } from 'react';

export const titlesMap = {
  error: {
    en: i18n.t('smsCampaign.requiredEngQuestion'),
    es: i18n.t('smsCampaign.requiredEspQuestion'),
  },
  name: {
    en: i18n.t('smsCampaign.yourNameEng'),
    es: i18n.t('smsCampaign.yourNameEsp'),
  },
  DOB: {
    en: i18n.t('smsCampaign.yourDOBEng'),
    es: i18n.t('smsCampaign.yourDOBEsp'),
  },
  email: {
    en: i18n.t('smsCampaign.preferEmailEng'),
    es: i18n.t('smsCampaign.preferEmailEsp'),
  },
} as const;

interface OutcomeFormProps {
  data: ICampaignQuestionAnswersSelect[];
  patientLanguage: 'en' | 'es';
  formik: FormikProps<IOutcomesFormValues>;
  readonly?: boolean;
  patientName: string;
  patientDob: string;
  patientEmail: string;
  groupName?: TQuestionGroup;
}

const OutcomeForm: FC<OutcomeFormProps> = ({
  data,
  patientLanguage,
  formik,
  readonly,
  patientName,
  patientDob,
  patientEmail,
  groupName,
}) => {
  const readOnlyValue = (value: string) => (
    <span className={s['read-only-value']}>{value}</span>
  );

  return (
    <>
      {!readonly && (
        <HeaderBlock lang={patientLanguage} groupName={groupName} />
      )}
      <FormBlock
        title={titlesMap.name[patientLanguage]}
        divider={!!readonly}
        required
        description={null}
        showShadow={!readonly}
      >
        {readonly ? (
          readOnlyValue(patientName)
        ) : (
          <BaseInput value={patientName} disabled />
        )}
      </FormBlock>
      <FormBlock
        title={titlesMap.DOB[patientLanguage]}
        divider={!!readonly}
        required
        description={null}
        showShadow={!readonly}
      >
        {readonly ? (
          readOnlyValue(patientDob)
        ) : (
          <BaseInput value={patientDob} disabled />
        )}
      </FormBlock>
      <FormBlock
        title={titlesMap.email[patientLanguage]}
        divider={!!readonly}
        required
        description={null}
        showShadow={!readonly}
      >
        {readonly ? (
          readOnlyValue(patientEmail)
        ) : (
          <BaseInput value={patientEmail} disabled />
        )}
      </FormBlock>
      {data.map((question) => {
        return (
          <FormBlock
            key={question.guid}
            title={question.question}
            required={question.required}
            description={question.description}
            error={formik.errors[question.guid]}
            showShadow={!readonly}
            divider={question.type !== 'info'}
          >
            {question.answers.length === 0
              ? null
              : question.answers.map((answer) => {
                  const values = formik.values[question.guid]?.answers ?? [];
                  const otherAnswerGuid =
                    formik.values[question.guid]?.otherAnswerGuid;

                  return (
                    <FormItem
                      key={answer.guid}
                      answer={answer}
                      setFormikValues={formik.setFieldValue}
                      questionGuid={question.guid}
                      answerGuids={values}
                      label={answer.answer}
                      required={question.required}
                      readonly={readonly}
                      anyAnswer={formik.values[question.guid]?.anyAnswer}
                      otherAnswerGuid={otherAnswerGuid}
                      lang={patientLanguage}
                    />
                  );
                })}
          </FormBlock>
        );
      })}
    </>
  );
};
export default OutcomeForm;
